import React, { useState } from 'react';
import { useTheme } from '../App';
import { useLocation, useNavigate } from 'react-router-dom';

function FileSystemSidebar() {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedFolders, setExpandedFolders] = useState({ root: true, subreddits: true });

  // Determine if current path is home page. We still track this so we can dim
  // the appearance of sidebar links when not on the home page, but clicking
  // any file item will still work and automatically return home first.
  const isHomePage = location.pathname === '/';

  const fileSystem = [
    {
      name: 'reddit',
      type: 'folder',
      icon: '󰉋',
      children: [
        { name: 'home', type: 'file', icon: '󰋜', command: 'home' },
        { name: 'popular', type: 'file', icon: '󰈸', command: 'popular' },
        { name: 'news', type: 'file', icon: '󰋼', command: 'news' },
        {
          name: 'subreddits',
          type: 'folder',
          icon: '󰉋',
          children: [
            { name: 'programming', type: 'file', icon: '󰨞', command: 'programming' },
            { name: 'technology', type: 'file', icon: '󰪶', command: 'technology' },
            { name: 'science', type: 'file', icon: '󰪺', command: 'science' },
            { name: 'gaming', type: 'file', icon: '󰊗', command: 'gaming' },
            { name: 'todayilearned', type: 'file', icon: '󰛨', command: 'todayilearned' },
            { name: 'worldnews', type: 'file', icon: '󰖬', command: 'worldnews' },
            { name: 'askreddit', type: 'file', icon: '󰩭', command: 'askreddit' },
          ],
        },
      ],
    },
  ];
  // Navigate home and dispatch a command to the Home page. This helper is
  // reused by the dedicated home button as well as file items. It ensures that
  // commands issued from other pages (e.g., Search) will work by navigating
  // back to the Home route before firing the event. When already on the home
  // page the navigation is a no-op.
  const goHomeAndExecute = (command) => {
    if (!isHomePage) {
      navigate('/');
    }
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('fileSystemCommand', { detail: command }));
    }, 10);
  };

  const toggleFolder = (path) => {
    setExpandedFolders(prev => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const handleFileClick = (command) => {
    // Handle a click on a file item. Instead of ignoring clicks when not on the
    // home page, always call goHomeAndExecute so that navigation and command
    // dispatch happen correctly.
    goHomeAndExecute(command);
  };

  const renderTree = (items, depth = 0, parentPath = '') => {
    return items.map((item, index) => {
      const path = `${parentPath}/${item.name}`;
      const isExpanded = expandedFolders[path];

      return (
        <div key={index}>
          <div
            style={{
              padding: '3px 5px 3px ' + (depth * 20 + 10) + 'px',
              // Always allow clicking file items. When not on home page we will
              // navigate home automatically, so pointer is always enabled.
              cursor: 'pointer',
              // Dim the color of file items when not on the home page to give a
              // subtle visual cue that navigation will occur.
              color: (item.type === 'folder' || isHomePage)
                ? theme.colors.foreground
                : theme.colors.foreground + '80',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              if (item.type === 'folder' || isHomePage) {
                e.target.style.backgroundColor = theme.colors.background2;
              }
            }}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            onClick={() => {
              if (item.type === 'folder') {
                toggleFolder(path);
              } else {
                handleFileClick(item.command);
              }
            }}
          >
            {item.type === 'folder' && (
              <span style={{ color: theme.colors.accentCyan }}>
                {isExpanded ? '󰁊' : '󰁈'}
              </span>
            )}
            <span style={{ color: item.type === 'folder' ? theme.colors.accentCyan : theme.colors.accentGreen }}>
              {item.icon}
            </span>
            <span style={{ fontSize: '12px' }}>{item.name}</span>
          </div>
          {item.type === 'folder' && isExpanded && item.children && (
            <div>{renderTree(item.children, depth + 1, path)}</div>
          )}
        </div>
      );
    });
  };

  return (
    <div style={{
      width: '250px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.background,
      padding: '10px',
      fontFamily: 'CaskaydiaCove Nerd Font, monospace',
      // Use a slightly smaller font size for a tighter sidebar
      fontSize: '12px',
      boxShadow: `0 0 10px ${theme.colors.shadow}`,
      height: 'calc(100vh - 40px)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Dedicated Home button so users can always return to the root feed. */}
      <div
        onClick={() => goHomeAndExecute('home')}
        style={{
          padding: '6px 10px',
          marginBottom: '8px',
          cursor: 'pointer',
          backgroundColor: theme.colors.background2,
          color: theme.colors.accentGreen,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '13px',
        }}
      >
        <span style={{ color: theme.colors.accentCyan }}>󰮭</span>
        <span>Home</span>
      </div>
      <div style={{
        borderBottom: `1px solid ${theme.colors.border}`,
        paddingBottom: '10px',
        marginBottom: '10px',
        color: theme.colors.accentGreen,
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
      }}>
        <span>󰉋</span>
        <span>File Explorer</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {renderTree(fileSystem)}
      </div>
    </div>
  );
}

export default FileSystemSidebar;